import React, { useMemo } from 'react';
import { useOutletContext } from 'react-router-dom';
import { Plane, Sun } from 'lucide-react';
import type { FinancialAidStaffDetailContext } from '../ApplicantDetail';
import { asArray, asRecord } from '../../utils/domainData';
import {
  describeRawAmountWithDetails,
  firstNonEmptyRawText,
  readParentCollectedFinancialAidSnapshot,
  readRawAmount,
  readRawBoolean,
  readRawText,
  readSnapshotArray,
  readSnapshotRecord,
} from './staffFinancialAidRawSnapshot';
import {
  type FinancialAidDisplayYears,
  resolveFinancialAidDisplayYears,
} from '../../../../financial-aid/financialAidAcademicYear';

const readText = (value: unknown, fallback = ''): string => {
  const next = String(value ?? '').trim();
  return next || fallback;
};

const summerCampReviewHeading = (year: number | null, fallback: string): string =>
  year ? `SUMMER ${year} ${fallback}` : fallback.replace(/[()]/g, '');

const asNumber = (value: unknown): number => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

type SummerCampEntry = {
  child: string;
  camp: string;
  projectedChild?: string;
  projectedCamp?: string;
  actualChild?: string;
  actualCamp?: string;
  projected2025: number;
  actual2024: number;
};

export type ExpensesView = {
  householdExpenses: {
    totalMonthly: number;
    ratioVsIncome: number;
    breakdown: Record<string, number>;
    medicalCoverage: string | null;
  };
  discretionarySpending: {
    majorExpenditures: {
      homeImprovements: string | null;
      largePurchases: string | null;
      extraordinaryObligations: string | null;
      servicesToYST: string | null;
    };
    summerCamps: {
      attendance: boolean;
      entries: SummerCampEntry[];
      displayYears: FinancialAidDisplayYears;
    };
    vacation: {
      totalCost: number;
      details: string;
      participants: {
        parents: string;
        children: string;
      };
    };
  };
};

const labelForCategory = (category: string, description = ''): string => {
  const normalized = `${category} ${description}`.toLowerCase();
  if (normalized.includes('utilit')) return 'utilities';
  if (normalized.includes('shul') || normalized.includes('dues')) return 'shulDues';
  if (normalized.includes('clean')) return 'cleaningHelp';
  if (normalized.includes('tuition')) return 'tuition';
  if (normalized.includes('food')) return 'food';
  if (normalized.includes('health')) return 'healthInsurance';
  if (normalized.includes('housing')) return 'housing';
  if (normalized.includes('transport')) return 'transportation';
  if (normalized.includes('insurance')) return 'insurance';
  if (normalized.includes('discretionary')) return 'discretionary';
  return category || 'other';
};

const labelForBreakdownKey = (key: string): string => {
  if (key === 'healthInsurance') return 'Health Insurance';
  if (key === 'shulDues') return 'Shul Dues';
  if (key === 'cleaningHelp') return 'Cleaning Help';
  if (key === 'insurance') return 'Car + Life Insurance';
  return key.replace(/([A-Z])/g, ' $1').trim().replace(/\b\w/g, (letter) => letter.toUpperCase());
};

const approximatelyEqualCurrency = (first: number, second: number): boolean =>
  Math.abs(first - second) < 0.01;

const duplicateGranularHealthInsuranceTotal = (
  expenses: Record<string, unknown>,
  breakdown: Record<string, number>
): number => {
  let duplicateTotal = 0;
  const aggregateHealthcare = asNumber(expenses.healthcareTotal)
    || (asNumber(expenses.healthInsurance) + asNumber(expenses.medicalCoverage));
  const aggregateInsurance = asNumber(expenses.insuranceTotal);

  if (
    aggregateHealthcare > 0
    && approximatelyEqualCurrency(aggregateHealthcare, asNumber(breakdown.healthInsurance))
  ) {
    duplicateTotal += aggregateHealthcare;
  }

  if (
    aggregateInsurance > 0
    && approximatelyEqualCurrency(aggregateInsurance, asNumber(breakdown.insurance))
  ) {
    duplicateTotal += aggregateInsurance;
  }

  return duplicateTotal;
};

const normalizeStaffCorrectionPath = (value: unknown): string =>
  readText(value)
    .replace(/^application\.sections\./, '')
    .replace(/^sections\./, '');

const staffCorrectionSetsAnyPathToZeroOrBlank = (
  sections: Record<string, unknown>,
  targetPaths: string[],
): boolean => {
  const normalizedTargetPaths = new Set(targetPaths.map(normalizeStaffCorrectionPath));
  const additionalInfo = asRecord(sections.additionalInfo);
  return asArray<Record<string, unknown>>(additionalInfo.staffCorrections).some((correction) => {
    const changes = asArray<Record<string, unknown>>(correction.changes);
    const changedPaths = new Set(changes.map((change) => normalizeStaffCorrectionPath(change.path)));
    const zeroedChange = changes.some((change) =>
      normalizedTargetPaths.has(normalizeStaffCorrectionPath(change.path))
      && (!hasAmountValue(change.newValue) || asNumber(change.newValue) <= 0)
    );
    if (zeroedChange) {
      return true;
    }
    return asArray<unknown>(correction.paths).some((path) => {
      const normalizedPath = normalizeStaffCorrectionPath(path);
      return normalizedTargetPaths.has(normalizedPath) && !changedPaths.has(normalizedPath);
    });
  });
};

const buildRawCampEntries = (rawSnapshot: Record<string, unknown>): SummerCampEntry[] => {
  const projectedEntries = readSnapshotArray(rawSnapshot, 'camps2025');
  const actualEntries = readSnapshotArray(rawSnapshot, 'camps2024');
  const rowCount = Math.max(projectedEntries.length, actualEntries.length);

  return Array.from({ length: rowCount }, (_, index) => {
    const projected = projectedEntries[index] ?? {};
    const actual = actualEntries[index] ?? {};
    const projectedChild = firstNonEmptyRawText(projected.childName, projected.child);
    const projectedCamp = firstNonEmptyRawText(projected.campName, projected.camp);
    const actualChild = firstNonEmptyRawText(actual.childName, actual.child);
    const actualCamp = firstNonEmptyRawText(actual.campName, actual.camp);
    const projected2025 = readRawAmount(projected.amount) || readRawAmount(projected.fee);
    const actual2024 = readRawAmount(actual.amount) || readRawAmount(actual.fee);
    return {
      child: projectedChild || actualChild || 'Child',
      camp: projectedCamp || actualCamp || 'Camp',
      projectedChild: projectedChild || undefined,
      projectedCamp: projectedCamp || undefined,
      actualChild: actualChild || undefined,
      actualCamp: actualCamp || undefined,
      projected2025,
      actual2024,
    };
  }).filter((entry) =>
    entry.projected2025 > 0
    || entry.actual2024 > 0
    || Boolean(entry.projectedChild || entry.projectedCamp || entry.actualChild || entry.actualCamp)
  );
};

const summerCampEntryKey = (entry: SummerCampEntry): string => {
  const child = readText(entry.child).toLowerCase();
  const camp = readText(entry.camp).toLowerCase();
  if (!child || !camp || child === 'child' || camp === 'camp') {
    return '';
  }
  return `${child}::${camp}`;
};

const mergeSummerCampEntries = (
  normalizedEntries: SummerCampEntry[],
  rawEntries: SummerCampEntry[],
): SummerCampEntry[] => {
  if (normalizedEntries.length === 0) {
    return rawEntries;
  }

  const matchedRawIndexes = new Set<number>();
  const mergedEntries = normalizedEntries.map((entry) => {
    const key = summerCampEntryKey(entry);
    const rawIndex = key
      ? rawEntries.findIndex((rawEntry, index) =>
        !matchedRawIndexes.has(index) && summerCampEntryKey(rawEntry) === key)
      : -1;
    if (rawIndex < 0) {
      return entry;
    }
    matchedRawIndexes.add(rawIndex);
    const rawEntry = rawEntries[rawIndex];
    return {
      ...entry,
      projectedChild: entry.projectedChild || rawEntry.projectedChild,
      projectedCamp: entry.projectedCamp || rawEntry.projectedCamp,
      actualChild: entry.actualChild || rawEntry.actualChild,
      actualCamp: entry.actualCamp || rawEntry.actualCamp,
      projected2025: entry.projected2025 > 0 ? entry.projected2025 : rawEntry.projected2025,
      actual2024: entry.actual2024 > 0 ? entry.actual2024 : rawEntry.actual2024,
    };
  });

  return [
    ...mergedEntries,
    ...rawEntries.filter((_, index) => !matchedRawIndexes.has(index)),
  ];
};

const describeMajorExpenditureEntry = (
  entry: Record<string, unknown> | undefined,
  fallbackAmount: unknown,
  fallbackDetails: unknown,
): string | null =>
  describeRawAmountWithDetails(
    entry?.expectedAmount ?? entry?.value ?? fallbackAmount,
    entry?.reason ?? fallbackDetails,
    readText(entry?.reason, readText(entry?.description)) || null,
  );

const fillBreakdownFallback = (
  breakdown: Record<string, number>,
  key: string,
  amount: number,
  authoritativeKeys?: ReadonlySet<string>,
): void => {
  if (authoritativeKeys?.has(key)) {
    return;
  }
  if ((breakdown[key] || 0) <= 0 && amount > 0) {
    breakdown[key] = amount;
  }
};

const hasAmountValue = (value: unknown): boolean =>
  value !== undefined && value !== null && readText(value).length > 0;

const applyBreakdownAuthoritativeAmount = (
  breakdown: Record<string, number>,
  authoritativeKeys: Set<string>,
  key: string,
  value: unknown,
): void => {
  if (hasAmountValue(value)) {
    breakdown[key] = asNumber(value);
    authoritativeKeys.add(key);
  }
};

const hasOwnRecordKey = (record: Record<string, unknown>, key: string): boolean =>
  Object.prototype.hasOwnProperty.call(record, key);

const readBooleanLike = (value: unknown): boolean => {
  if (typeof value === 'boolean') return value;
  return /^(true|yes|y|1)$/i.test(readText(value));
};

const describeServicesToYST = (source: Record<string, unknown>): string | null => {
  const description = readText(source.servicesDescription);
  if (!hasOwnRecordKey(source, 'servicesToYST')) {
    return description || null;
  }
  return readBooleanLike(source.servicesToYST) ? description || 'Yes' : 'No';
};

export const buildExpensesView = (review: FinancialAidStaffDetailContext['review']): ExpensesView => {
  const reviewRecord = asRecord(review as unknown);
  const application = asRecord(reviewRecord.application);
  const sections = asRecord(application.sections);
  const metrics = asRecord(application.metrics);
  const displayYears = resolveFinancialAidDisplayYears(application.academicYear);

  const expenses = asRecord(sections.expenses);
  const rawSnapshot = readParentCollectedFinancialAidSnapshot(review);
  const rawExpenses = readSnapshotRecord(rawSnapshot, 'expenses');
  const rawAssets = readSnapshotRecord(rawSnapshot, 'assets');
  const rawMajorExp = readSnapshotRecord(rawSnapshot, 'majorExp');
  const rawVacation = readSnapshotRecord(rawSnapshot, 'vacation');
  const expenseEntries = asArray<Record<string, unknown>>(expenses.entries);
  const breakdown: Record<string, number> = {
    housing: 0,
    utilities: 0,
    food: 0,
    healthInsurance: 0,
    shulDues: 0,
    cleaningHelp: 0,
    tuition: 0,
  };
  const authoritativeBreakdownKeys = new Set<string>();
  const appliedExpenseSourceFieldKeys = new Set<string>();

  for (const entry of expenseEntries) {
    const sourceRequirementFieldKey = readText(entry.sourceRequirementFieldKey);
    if (sourceRequirementFieldKey) {
      if (appliedExpenseSourceFieldKeys.has(sourceRequirementFieldKey)) {
        continue;
      }
      appliedExpenseSourceFieldKeys.add(sourceRequirementFieldKey);
    }
    const category = labelForCategory(
      readText(entry.category).replace(/[_-]+/g, ' '),
      readText(entry.description).replace(/[_-]+/g, ' ')
    );
    const amount = asNumber(entry.monthlyAmount);
    breakdown[category] = (breakdown[category] || 0) + amount;
  }

  const rawCleaningHelp = readRawAmount(rawExpenses.cleaningHelp);
  const rawShulDues = readRawAmount(rawExpenses.shulDues);
  const rawOtherExpense = readRawAmount(rawExpenses.other);
  const medicalCoverage = readText(
    expenses.medicalCoverage,
    readText(rawExpenses.medicalCoverage),
  ) || null;
  const rawOtherSplitTotal = rawCleaningHelp + rawShulDues;
  const canSplitGenericOther = expenseEntries.length > 0
    && rawOtherSplitTotal > 0
    && (breakdown.other || 0) > 0
    && (breakdown.shulDues || 0) <= 0
    && (breakdown.cleaningHelp || 0) <= 0
    && approximatelyEqualCurrency((breakdown.other || 0), rawOtherExpense + rawOtherSplitTotal);
  if (canSplitGenericOther) {
    breakdown.other = rawOtherExpense;
  }
  const canFillRawDetailedOther = canSplitGenericOther || (breakdown.other || 0) <= 0;

  const canonicalHealthInsurance = hasAmountValue(expenses.healthInsurance)
    ? expenses.healthInsurance
    : expenses.healthcareTotal;
  const rawHealthInsurance = readRawAmount(rawExpenses.healthInsurance);
  const rawCarInsurance = readRawAmount(rawExpenses.carInsurance);
  const rawLifeInsurance = readRawAmount(rawExpenses.lifeInsurance);
  const insuranceTotalWasStaffZeroed = staffCorrectionSetsAnyPathToZeroOrBlank(sections, [
    'expenses.insuranceTotal',
  ]);
  const rawCarInsuranceWasStaffZeroed = staffCorrectionSetsAnyPathToZeroOrBlank(sections, [
    'additionalInfo.parentCollectedFinancialAidSnapshot.expenses.carInsurance',
    'parentCollectedFinancialAidSnapshot.expenses.carInsurance',
  ]);
  const rawLifeInsuranceWasStaffZeroed = staffCorrectionSetsAnyPathToZeroOrBlank(sections, [
    'additionalInfo.parentCollectedFinancialAidSnapshot.expenses.lifeInsurance',
    'parentCollectedFinancialAidSnapshot.expenses.lifeInsurance',
  ]);
  const rawCarAndLifeInsurance =
    (rawCarInsuranceWasStaffZeroed ? 0 : rawCarInsurance)
    + (rawLifeInsuranceWasStaffZeroed ? 0 : rawLifeInsurance);
  const carAndLifeInsuranceWasStaffZeroed =
    insuranceTotalWasStaffZeroed
    || ((rawCarInsurance <= 0 || rawCarInsuranceWasStaffZeroed)
      && (rawLifeInsurance <= 0 || rawLifeInsuranceWasStaffZeroed));
  const normalizedCarAndLifeInsurance = (() => {
    if (!hasAmountValue(expenses.insuranceTotal)) {
      return expenses.insuranceTotal;
    }
    const canonicalAmount = asNumber(expenses.insuranceTotal);
    return canonicalAmount <= 0 && rawCarAndLifeInsurance > 0 && !carAndLifeInsuranceWasStaffZeroed
      ? undefined
      : expenses.insuranceTotal;
  })();
  const normalizedHealthInsurance = (() => {
    if (!hasAmountValue(canonicalHealthInsurance)) {
      return canonicalHealthInsurance;
    }
    const canonicalAmount = asNumber(canonicalHealthInsurance);
    if (
      rawHealthInsurance > 0
      && canonicalAmount > rawHealthInsurance
      && approximatelyEqualCurrency(canonicalAmount, rawHealthInsurance * 2)
    ) {
      return rawHealthInsurance;
    }
    return canonicalHealthInsurance;
  })();

  applyBreakdownAuthoritativeAmount(breakdown, authoritativeBreakdownKeys, 'housing', expenses.housingCost);
  fillBreakdownFallback(
    breakdown,
    'housing',
    readRawAmount(rawExpenses.housing)
      || readRawAmount(rawAssets.mortgagePayment)
      || readRawAmount(rawAssets.rentPayment),
    authoritativeBreakdownKeys,
  );
  applyBreakdownAuthoritativeAmount(breakdown, authoritativeBreakdownKeys, 'utilities', expenses.utilitiesTotal);
  fillBreakdownFallback(
    breakdown,
    'utilities',
    readRawAmount(rawExpenses.utilities)
      || readRawAmount(rawAssets.utilities),
    authoritativeBreakdownKeys,
  );
  applyBreakdownAuthoritativeAmount(breakdown, authoritativeBreakdownKeys, 'food', expenses.foodTotal);
  fillBreakdownFallback(breakdown, 'food', readRawAmount(rawExpenses.food), authoritativeBreakdownKeys);
  applyBreakdownAuthoritativeAmount(breakdown, authoritativeBreakdownKeys, 'healthInsurance', normalizedHealthInsurance);
  fillBreakdownFallback(
    breakdown,
    'healthInsurance',
    rawHealthInsurance,
    authoritativeBreakdownKeys,
  );
  applyBreakdownAuthoritativeAmount(breakdown, authoritativeBreakdownKeys, 'insurance', normalizedCarAndLifeInsurance);
  fillBreakdownFallback(
    breakdown,
    'insurance',
    rawCarAndLifeInsurance,
    authoritativeBreakdownKeys,
  );
  fillBreakdownFallback(
    breakdown,
    'cleaningHelp',
    (canFillRawDetailedOther ? rawCleaningHelp : 0)
      || (expenseEntries.length === 0 ? asNumber(expenses.otherTotal) : 0)
  );
  fillBreakdownFallback(
    breakdown,
    'shulDues',
    asNumber(expenses.shulDuesTotal) || (canFillRawDetailedOther ? rawShulDues : 0)
  );
  fillBreakdownFallback(
    breakdown,
    'tuition',
    readRawAmount(rawExpenses.tuition),
    authoritativeBreakdownKeys,
  );

  const totalMonthlyFromBreakdown = Object.values(breakdown).reduce((sum, value) => sum + asNumber(value), 0);
  const reportedTotalMonthly = asNumber(expenses.totalMonthlyExpenses)
    || asNumber(metrics.totalMonthlyExpenses)
    || totalMonthlyFromBreakdown;
  const duplicateHealthInsuranceTotal = expenseEntries.length > 0
    ? duplicateGranularHealthInsuranceTotal(expenses, breakdown)
    : 0;
  const hasInflatedHealthInsuranceOnlyTotal = rawHealthInsurance > 0
    && reportedTotalMonthly > totalMonthlyFromBreakdown
    && approximatelyEqualCurrency(reportedTotalMonthly, rawHealthInsurance * 2)
    && approximatelyEqualCurrency(totalMonthlyFromBreakdown, rawHealthInsurance);
  const totalMonthly = hasInflatedHealthInsuranceOnlyTotal
    ? totalMonthlyFromBreakdown
    : duplicateHealthInsuranceTotal > 0
    && reportedTotalMonthly > totalMonthlyFromBreakdown
    && reportedTotalMonthly - duplicateHealthInsuranceTotal >= totalMonthlyFromBreakdown
      ? reportedTotalMonthly - duplicateHealthInsuranceTotal
      : reportedTotalMonthly;

  const totalAnnualIncome = asNumber(metrics.totalAnnualIncome)
    || asNumber(asRecord(sections.income).totalAnnualIncome);

  const ratioVsIncome = totalAnnualIncome > 0
    ? (totalMonthly * 12) / totalAnnualIncome
    : 0;

  const majorExpenditures = asRecord(sections.majorExpenditures);
  const majorEntries = asArray<Record<string, unknown>>(majorExpenditures.entries);
  const specialCircumstances = asRecord(sections.specialCircumstances);

  const parent1 = asRecord(sections.parent1);
  const parent2 = asRecord(sections.parent2);
  const ystStudents = asArray<Record<string, unknown>>(sections.ystStudents);

  const normalizedCampEntries = asArray<Record<string, unknown>>(
    asRecord(sections.additionalInfo).summerCamps
      ? asRecord(asRecord(sections.additionalInfo).summerCamps).entries
      : []
  )
    .map((entry) => ({
      child: readText(entry.childName, readText(entry.child, 'Child')),
      camp: readText(entry.campName, readText(entry.camp, 'Camp')),
      projectedChild: readText(entry.projectedChild, readText(entry.childName, readText(entry.child))) || undefined,
      projectedCamp: readText(entry.projectedCamp, readText(entry.campName, readText(entry.camp))) || undefined,
      actualChild: readText(entry.actualChild, readText(entry.childName, readText(entry.child))) || undefined,
      actualCamp: readText(entry.actualCamp, readText(entry.campName, readText(entry.camp))) || undefined,
      projected2025: asNumber(entry.projected2025) || asNumber(entry.projectedCost),
      actual2024: asNumber(entry.actual2024) || asNumber(entry.actualCost),
    }))
    .filter((entry) => entry.child.length > 0 && entry.camp.length > 0);
  const rawCampEntries = buildRawCampEntries(rawSnapshot);
  const campEntries = mergeSummerCampEntries(normalizedCampEntries, rawCampEntries);

  const rawVacationDetail = [
    readRawBoolean(rawVacation.traveled) ? 'Family traveled' : '',
    firstNonEmptyRawText(rawVacation.destinations),
  ].filter(Boolean).join(': ');
  const vacationDetail = readText(
    asRecord(sections.additionalInfo).vacationDetails,
    readText(specialCircumstances.additionalCircumstances, rawVacationDetail)
  );
  const vacationCost = asNumber(asRecord(sections.additionalInfo).vacationCost)
    || readRawAmount(rawVacation.cost);

  const parentNames = [
    `${readText(parent1.firstName)} ${readText(parent1.lastName)}`.trim(),
    `${readText(parent2.firstName)} ${readText(parent2.lastName)}`.trim(),
  ].filter(Boolean).join(', ');
  const otherChildNames = readSnapshotArray(rawSnapshot, 'otherChildren')
    .map((child) => firstNonEmptyRawText(child.name, child.studentName))
    .filter(Boolean);
  const childNames = [
    ...ystStudents
    .map((student) => `${readText(student.firstName)} ${readText(student.lastName)}`.trim())
      .filter(Boolean),
    ...otherChildNames,
  ]
    .filter((name, index, names) => names.indexOf(name) === index)
    .join(', ');
  const parentTravelFlagProvided = Object.prototype.hasOwnProperty.call(rawVacation, 'parentsTraveled');
  const childTravelFlagProvided = Object.prototype.hasOwnProperty.call(rawVacation, 'childrenTraveled');
  const parentParticipants = parentTravelFlagProvided
    ? (readRawBoolean(rawVacation.parentsTraveled) ? parentNames || 'Parents' : 'N/A')
    : parentNames || 'N/A';
  const childParticipants = childTravelFlagProvided
    ? (readRawBoolean(rawVacation.childrenTraveled) ? childNames || 'Children' : 'N/A')
    : childNames || 'N/A';

  return {
    householdExpenses: {
      totalMonthly,
      ratioVsIncome,
      breakdown,
      medicalCoverage,
    },
    discretionarySpending: {
      majorExpenditures: {
        homeImprovements:
          describeMajorExpenditureEntry(majorEntries[0], rawMajorExp.homeImprovements, rawMajorExp.homeImprovementsDetails)
          || null,
        largePurchases:
          describeMajorExpenditureEntry(majorEntries[1], rawMajorExp.largePurchases, rawMajorExp.largePurchasesDetails)
          || null,
        extraordinaryObligations:
          readText(specialCircumstances.unusualExpensesDescription)
          || readText(specialCircumstances.additionalCircumstances)
          || describeRawAmountWithDetails(
            rawMajorExp.loanAmount,
            rawMajorExp.extraordinaryObligations,
            readRawText(rawMajorExp.extraordinaryObligations) || null
          )
          || null,
        servicesToYST:
          describeServicesToYST(majorExpenditures)
          || describeServicesToYST(rawMajorExp)
          || null,
      },
      summerCamps: {
        attendance: campEntries.length > 0,
        entries: campEntries,
        displayYears,
      },
      vacation: {
        totalCost: vacationCost,
        details: vacationDetail,
        participants: {
          parents: parentParticipants,
          children: childParticipants,
        },
      },
    },
  };
};

export const ExpensesTab: React.FC = () => {
  const { review, layoutMode } = useOutletContext<FinancialAidStaffDetailContext>();
  const isCompactLayout = layoutMode === 'compact';

  const { householdExpenses, discretionarySpending } = useMemo(
    () => buildExpensesView(review),
    [review]
  );
  const displayYears = discretionarySpending.summerCamps.displayYears;

  const formatCurrency = (val: number) =>
    val.toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 });

  const getRatioColor = (ratio: number) => {
    if (ratio <= 0.5) return '#92EEBA';
    if (ratio <= 0.7) return '#F59E0B';
    return '#FF5F5F';
  };

  if (!review) {
    return <p className="text-sm text-[#6B7280]">Application data is syncing.</p>;
  }

  return (
    <div data-testid="financial-aid-expenses-spending-tab" className="space-y-8 max-w-5xl">
      <div className={`bg-[#1C3B3E] text-white rounded-xl p-6 flex flex-col ${isCompactLayout ? '' : 'md:flex-row'} justify-between items-start ${isCompactLayout ? '' : 'md:items-center'} gap-6`}>
        <div>
          <p className="text-xs font-semibold tracking-wider uppercase opacity-70 mb-2">TOTAL MONTHLY EXPENSES</p>
          <p className="text-3xl font-bold text-white">{formatCurrency(householdExpenses.totalMonthly)}</p>
        </div>
        <div className={isCompactLayout ? '' : 'text-right'}>
          <p className="text-xs font-semibold tracking-wider uppercase opacity-70 mb-2">RATIO VS INCOME</p>
          <p className="text-3xl font-bold" style={{ color: getRatioColor(householdExpenses.ratioVsIncome) }}>
            {(householdExpenses.ratioVsIncome * 100).toFixed(1)}%
          </p>
        </div>
      </div>

      <div className={`grid grid-cols-1 ${isCompactLayout ? '' : 'md:grid-cols-2'} gap-3`}>
        {Object.entries(householdExpenses.breakdown).map(([key, value]) => (
          <div key={key} className="bg-white rounded-lg p-4 flex justify-between items-center border-none">
            <span className="text-sm capitalize" style={{ color: '#6B7280' }}>
              {labelForBreakdownKey(typeof key === 'string' ? key : String(key))}
            </span>
            <span className="text-sm font-semibold text-[#111827]">
              {formatCurrency(value as number)}
            </span>
          </div>
        ))}
      </div>
      {householdExpenses.medicalCoverage ? (
        <div className="bg-white rounded-lg p-4 border-none">
          <span className="block text-xs font-semibold tracking-wider uppercase mb-2" style={{ color: '#6B7280' }}>
            Medical Coverage
          </span>
          <span className="text-sm font-semibold text-[#111827]">
            {householdExpenses.medicalCoverage}
          </span>
        </div>
      ) : null}

      <div className="border-t pt-8" style={{ borderColor: '#E5E7EB' }}>
        <h2 className="text-lg font-bold text-[#111827] mb-6">Discretionary Spending</h2>

        <div className="space-y-8">
          <div className={`grid grid-cols-1 ${isCompactLayout ? '' : 'md:grid-cols-4'} gap-3`}>
            <div className="rounded-xl p-5 bg-[#FAFBF8] border-none">
              <h3 className="text-xs font-semibold tracking-wider uppercase mb-2" style={{ color: '#404F4D' }}>HOME IMPROVEMENTS</h3>
              <p className="text-sm italic" style={{ color: '#9CA3AF' }}>{discretionarySpending.majorExpenditures.homeImprovements || 'None reported'}</p>
            </div>
            <div className="rounded-xl p-5 bg-[#FAFBF8] border-none">
              <h3 className="text-xs font-semibold tracking-wider uppercase mb-2" style={{ color: '#404F4D' }}>LARGE PURCHASES</h3>
              <p className="text-sm italic" style={{ color: '#9CA3AF' }}>{discretionarySpending.majorExpenditures.largePurchases || 'None reported'}</p>
            </div>
            <div className="rounded-xl p-5 bg-[#FAFBF8] border-none">
              <h3 className="text-xs font-semibold tracking-wider uppercase mb-2" style={{ color: '#404F4D' }}>EXTRAORDINARY OBLIGATIONS</h3>
              <p className="text-sm italic" style={{ color: '#9CA3AF' }}>{discretionarySpending.majorExpenditures.extraordinaryObligations || 'None reported'}</p>
            </div>
            <div className="rounded-xl p-5 bg-[#FAFBF8] border-none">
              <h3 className="text-xs font-semibold tracking-wider uppercase mb-2" style={{ color: '#404F4D' }}>SERVICES TO YST</h3>
              <p className="text-sm italic" style={{ color: '#9CA3AF' }}>{discretionarySpending.majorExpenditures.servicesToYST || 'None reported'}</p>
            </div>
          </div>

          <div className="bg-white rounded-xl border p-6" style={{ borderColor: '#E5E7EB' }}>
            <div className="flex items-center gap-3 mb-6">
              <Sun size={18} style={{ color: '#404F4D' }} />
              <div>
                <h2 className="text-lg font-semibold text-[#111827]">Summer Camps</h2>
                <p className="text-xs text-[#6B7280]">Camp Attendance: {discretionarySpending.summerCamps.attendance ? 'Yes' : 'No'}</p>
              </div>
            </div>
            <div className={`grid grid-cols-1 ${isCompactLayout ? '' : 'md:grid-cols-2'} gap-3`}>
              <div>
                <h4 className="text-xs font-semibold uppercase mb-3" style={{ color: '#404F4D' }}>{summerCampReviewHeading(displayYears.currentCampYear, '(PROJECTED)')}</h4>
                <div className="space-y-3">
                  {discretionarySpending.summerCamps.entries.map((entry, index) => (
                    <div key={`projected-${index}`} className="rounded-lg p-4 flex justify-between items-center border-none" style={{ backgroundColor: '#F9FAFB' }}>
                      <span className="text-sm font-medium">{entry.projectedChild || entry.child} ({entry.projectedCamp || entry.camp})</span>
                      <span className="text-sm font-semibold">{formatCurrency(entry.projected2025)}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div>
                <h4 className="text-xs font-semibold uppercase mb-3" style={{ color: '#404F4D' }}>{summerCampReviewHeading(displayYears.priorCampYear, '(ACTUAL)')}</h4>
                <div className="space-y-3">
                  {discretionarySpending.summerCamps.entries.map((entry, index) => (
                    <div key={`actual-${index}`} className="rounded-lg p-4 flex justify-between items-center border-none" style={{ backgroundColor: '#F9FAFB' }}>
                      <span className="text-sm font-medium">{entry.actualChild || entry.child} ({entry.actualCamp || entry.camp})</span>
                      <span className="text-sm font-semibold text-[#6B7280]">{formatCurrency(entry.actual2024)}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl border p-6" style={{ borderColor: '#E5E7EB' }}>
            <div className="flex items-center gap-3 mb-6">
              <Plane size={18} style={{ color: '#404F4D' }} />
              <h2 className="text-lg font-semibold text-[#111827]">Vacation Info</h2>
            </div>
            <p className="text-sm italic mb-6" style={{ color: '#9CA3AF' }}>{discretionarySpending.vacation.details || 'No details provided'}</p>
            <div className={`grid grid-cols-1 ${isCompactLayout ? '' : 'md:grid-cols-2'} gap-3`}>
              <div className="rounded-lg border-none p-4 bg-[#FAFBF8]">
                <p className="text-xs font-semibold uppercase mb-1" style={{ color: '#6B7280' }}>COST</p>
                <p className="text-lg font-bold text-[#111827]">{formatCurrency(discretionarySpending.vacation.totalCost)}</p>
              </div>
              <div className="rounded-lg border-none p-4 bg-[#FAFBF8]">
                <p className="text-xs font-semibold uppercase mb-2" style={{ color: '#6B7280' }}>PARTICIPANTS</p>
                <p className="text-sm text-[#111827]"><span className="text-[#6B7280]">Parents:</span> {discretionarySpending.vacation.participants.parents || 'N/A'}</p>
                <p className="text-sm text-[#111827]"><span className="text-[#6B7280]">Children:</span> {discretionarySpending.vacation.participants.children || 'N/A'}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
